class ProductPersonalizationTrigger extends HTMLElement {
  constructor() {
    super();
    this.openButton = document.querySelector('[data-js-product-personalization]');
    this.closeButton = this.querySelector(
      '[data-js-product-personalization-close]'
    );
    this.overlay = this.querySelector(
      '[data-js-product-personalization-dialog]'
    );
    this.popupContent = this.querySelector('[data-js-dialog-drawer]');

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
  }

  connectedCallback() {
    if (this.openButton && this.overlay && this.closeButton) {
      this.openButton.addEventListener('click', this.open);
      this.closeButton.addEventListener('click', this.close);
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }
  }

  disconnectedCallback() {
    if (this.openButton && this.overlay && this.closeButton) {
      this.openButton.removeEventListener('click', this.open);
      this.closeButton.removeEventListener('click', this.close);
      this.overlay.removeEventListener('click', this.handleOverlayClick);
    }
  }

  open() {
    this.overlay.style.display="block"
    document.body.style.overflow = 'hidden'; 
  }

  close() {
    this.overlay.style.display="none"
    document.body.style.overflow = ''; 
  }

  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.close();
    }
  }
}

customElements.define('product-personalization-trigger', ProductPersonalizationTrigger);

class MonogramValidator extends HTMLElement {
    connectedCallback() {
      const inputId = this.getAttribute('input-id');
      if (!inputId) {
        return;
      }
      this.inputElement = this.querySelector('input');

      if (this.inputElement) {
        this.inputElement.addEventListener('input', this.validateInput.bind(this));
      }
    }
    validateInput() {
        const inputValue = this.inputElement.value;
        const isValidInput = /^[a-zA-Z0-9 .,?!:;"'-]{0,4}$/.test(inputValue);

        if (inputValue.length > 0 && inputValue.length < 5) {
            this.inputElement.classList.add('first-step-valid');
            if (isValidInput) {
                this.inputElement.classList.add('second-step-valid');
            } else {
                this.inputElement.classList.remove('second-step-valid');
            }
        } else {
            this.inputElement.classList.remove('first-step-valid');
            this.inputElement.classList.remove('second-step-valid');
        }
    }

    disconnectedCallback() {
      if (this.inputElement) {
        this.inputElement.removeEventListener('input', this.validateInput.bind(this));
      }
    }
}

customElements.define('monogram-validator', MonogramValidator);


/**
 * PersonalizationForm extends HTMLElement to create a custom element for handling product personalization.
 * It manages user selections for color, position, font, and text, updates the UI, and handles adding personalized products to the cart.
 */
class PersonalizationForm extends HTMLElement {
  constructor() {
      super();

      this.DEV = true;

      this.overlay = document.querySelector('[data-js-product-personalization-dialog]');

      this.personalizationVariantId = this.dataset.personalizationId || null;
      this.openDrawerButtons = document.querySelectorAll('[data-js-product-personalization]');

      this.colorName = '';
      this.positionName = '';
      this.fontName = '';

      this.colorInputs = this.querySelectorAll('[data-color-input]');
      this.positionInputs = this.querySelectorAll('[data-position-input]');
      this.textInputs = this.querySelectorAll('[data-text-input]');
      this.fontInputs = this.querySelectorAll('[data-font-input]');

      this.outputColorParagraphs = this.querySelectorAll('[data-color-output]');
      this.outputPositionParagraphs = this.querySelectorAll('[data-position-output]');
      this.outputFontParagraphs = this.querySelectorAll('[data-font-output]');

      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      this.submitButtons = this.querySelectorAll('[data-submit-button]');
      this.resetButtons = this.querySelectorAll('[data-reset-button]');

      this.finalOutputs = document.querySelectorAll('[data-final-personalization-output]');
      this.mainSubmitButtons = document.querySelectorAll('[data-final-submit-button]');
      
      this.textButton = this.dataset.textButton || null;
      this.textModifyButton = this.dataset.textModifyButton || null;
      this.textError = this.dataset.textError || null;

      // Object to store the current personalization data
      this.personalizationData = {
        "color": null,
        "position": null,
        "font": null,
        "text": null,
      };
  }

  connectedCallback(){
    if (!this.personalizationVariantId || !this.textButton ) return;
    if (this.openDrawerButtons.length < 1 || this.finalOutputs.length < 1 || this.submitButtons.length < 1 || this.mainSubmitButtons.length < 1) return;

    // Set up event listeners for input changes
    this.setInputEvent();

    // Set up event listeners for reset buttons
    this.setResetEvent();

    // Set up event listeners for form submission
    this.setFormData();

    // Set up event listeners for main cart submission
    this.submitForm();

    // If all personalization data is already selected, enable the submit button
    if(this.checkFullObject()){
      this.setButton(false);
    }
  }

  /**
   * Sets up the event listeners for the main submit buttons that add items to the cart.
   * Handles the logic for adding both the personalized variant and the base product to the cart.
   */

  submitForm() {
      this.mainSubmitButtons.forEach(mainSubmitButton => {
          mainSubmitButton.addEventListener('click', (event) =>{
              if (this.checkFullObject()) {
                  event.preventDefault();
                  
                  this.changeAddToCartButton();

                  const form = event.target.closest("form");
                  const product_variant_id = form.querySelector('input[name="id"]').value;
                  const date = Date.now();
              
                  // Prepare form data for adding items to the cart
                  let formData = {
                    'sections': this.cart.getSectionsToRender().map((section) => section.id),
                    'sections_url': window.location.pathname,
                    'items': [
                      {
                        'id': this.personalizationVariantId,  // ID of the personalized variant
                        'quantity': 1,
                        properties: {
                          '_bundleId': `${product_variant_id}||${date}`, // Unique bundle ID
                          'color': this.personalizationData.color,
                          'position': this.personalizationData.position,
                          'font': this.personalizationData.font,
                          'text': this.personalizationData.text
                        }
                      },
                      {
                        'id': product_variant_id, // ID of the base product variant
                        'quantity': 1,
                        properties: {
                          '_bundleId': `${product_variant_id}||${date}` // Same bundle ID to link items
                        }
                      }
                    ]
                  };
                  // Send a POST request to Shopify's cart add API
                  fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData)
                  })
                  .then((response) => response.json())
                  .then(response => {
                      if (response.status == 422) {
                        this.outputError();
                        return
                      } 
                      const quickAddModal = document.querySelector('quick-add-modal');
                      const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                      
                      // Filter out the personalization variant from the response to get the base product data
                      let newResponse = response.items;
                      newResponse.sections = response.sections;
                      if (quickAddModal) {
                          document.body.addEventListener('modalClosed', () => {
                              setTimeout(() => { cartDrawer.renderContents(newResponse) });
                          }, { once: true });
                          quickAddModal.hide(true);
                      } else {
                          cartDrawer.renderContents(newResponse);
                      }
                  })
                  .catch((error) => {
                    console.error('Error:', error);
                  })
                  .finally(() => {
                      const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                      if (cartDrawer && cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
                      this.resetAddToCartButton();
                      this.resetPersonalizationData();
                  });
              } else {
                  //aggiunge il prodott normale
              }
          });
      });
  }

  outputError() {
      let originalText = null;
      this.mainSubmitButtons.forEach(mainSubmitButton => {
          originalText = mainSubmitButton.querySelector('span').textContent;
          mainSubmitButton.querySelector('span').textContent = this.textError;
          mainSubmitButton.style.background = "#555555";
          mainSubmitButton.style.cursor = "not-allowed";
          mainSubmitButton.disabled = true;
      })
      this.activateAndDisableError(originalText);
  }

  resetOutputError(originalText) {
    this.mainSubmitButtons.forEach(mainSubmitButton => {
        mainSubmitButton.querySelector('span').innerHTML = originalText;
        mainSubmitButton.style.background = "#000000";
        mainSubmitButton.style.cursor = "pointer";
        mainSubmitButton.disabled = false;
    })
  }

  activateAndDisableError(originalText) {
    setTimeout(() => {
        this.resetOutputError(originalText);
    }, 3000);
  }

  resetAddToCartButton() {
    this.mainSubmitButtons.forEach(mainSubmitButton => {
      mainSubmitButton.classList.remove('sticky-mobile-product-personalization');
      mainSubmitButton.querySelector('.personalization-loading')?.classList.add('hidden');
      mainSubmitButton.removeAttribute('aria-disabled');
    });
  }

  changeAddToCartButton() {
    this.mainSubmitButtons.forEach(mainSubmitButton => {
        mainSubmitButton.classList.add('sticky-mobile-product-personalization');
        mainSubmitButton.querySelector('.personalization-loading').classList.remove('hidden');
        mainSubmitButton.setAttribute('aria-disabled', true);
    });
  }

  /**
   * Sets up event listeners for the personalization form's submit buttons.
   * Validates personalization data, updates outer display, changes button text, and closes the panel.
   */

  setFormData() {
    this.submitButtons.forEach(submitButton => {
      submitButton.addEventListener('click', () =>{
        if(this.checkFullObject()){
            this.setOuterData();
            this.changeButtonText(true, this.textModifyButton);
            this.closePanel();
        } else {
          this.setError();
        }
      })
    });
  }

  /**
   * Sets up event listeners for the reset buttons.
   * Calls `resetPersonalizationData` when a reset button is clicked.
   */
  setResetEvent() {
      this.resetButtons.forEach(resetButton => {
          resetButton.addEventListener('click', () =>{
              this.resetPersonalizationData();
          })
      });
  };
  
    /**
   * Resets all personalization data to its initial null state.
   * Also updates the outer display, resets input fields, and changes the button text.
   */
  resetPersonalizationData() {
    this.personalizationData = {
        "color": null,
        "position": null,
        "font": null,
        "text": null,
      };
      this.setOuterData();
      this.resetInput();
      this.changeButtonText(false, this.textButton);
  }

  closePanel() {
    this.overlay.style.display="none"
    document.body.style.overflow = ''; 
  }

  /**
   * Sets up event listeners for all input fields (color, position, font, text).
   * Calls `handleInputChange` when an input's value changes.
   */
  setInputEvent() {
      this.colorInputs.forEach(colorInput => {
          colorInput.addEventListener('change', (event) => this.handleInputChange(event));
      });
      this.positionInputs.forEach(positionInput => {
          positionInput.addEventListener('change', (event) => this.handleInputChange(event));
      });
      this.fontInputs.forEach(fontInput => {
          fontInput.addEventListener('change', (event) => this.handleInputChange(event));
      });
      this.textInputs.forEach(textInput => {
          textInput.addEventListener('input', (event) => this.handleInputChange(event));
      });
  }

    /**
   * Resets the state of all personalization input fields.
   * Unchecks radio buttons, clears text inputs, and resets output paragraphs.
   */
  resetInput() {
      this.colorInputs.forEach(colorInput => {
          colorInput.checked = false;
      });
      this.positionInputs.forEach(positionInput => {
          positionInput.checked = false;
      });
      this.fontInputs.forEach(fontInput => {
          fontInput.checked = false;
      });
      this.textInputs.forEach(textInput => {
          textInput.value = '';
          textInput.dispatchEvent(new Event("input"));
      });
      
      this.resetOutputParagraphs ()
  }

  /**
   * Handles changes in personalization input fields.
   * Determines the type of input, updates `personalizationData`, displays selected values,
   * and enables/disables the submit button based on data completeness.
   * @param {Event} event The change or input event object.
   */
  handleInputChange(event) {
    let type = null;
    let outputContainers = null;

    // Determine the type of input based on data attributes
    if(event.target.hasAttribute('data-color-input')) { 
      type = 'color';
      outputContainers = this.outputColorParagraphs;
    }
    if(event.target.hasAttribute('data-position-input')) {
      type = 'position';
      outputContainers = this.outputPositionParagraphs;
    }
    if(event.target.hasAttribute('data-font-input')) {
      type = 'font';
      outputContainers = this.outputFontParagraphs;
    }
    if(event.target.hasAttribute('data-text-input')) {
      type = 'text';
    }

    // Special handling for color input (use data-input-name for display)
    let selectedValue = event.target.value;
    if (type == 'color') {
        selectedValue = event.target.dataset.inputName;
    }

    // Special handling for text input (validate length)
    if (type == 'text') {
      if(selectedValue.length < 1 || selectedValue.length > 4){
        selectedValue = null;
      }
    }
    this.personalizationData[type] = selectedValue;

    if(outputContainers) this.setOutputParagraphs(outputContainers, selectedValue);

    if(this.checkFullObject()){
      this.setButton(true);
    }
    else{
      this.setButton(false);
    } 
  }

  /**
   * Updates the text content of specified output elements with a given value.
   * @param {NodeList} targets A NodeList of elements to update.
   * @param {string} value The text content to set.
   */
  setOutputParagraphs(targets, value) {
    targets.forEach(target => {
      target.innerHTML = value;
    })
  }

  /**
   * Clears the text content of all output paragraphs (color, position, font).
   */
  resetOutputParagraphs () {
    this.outputColorParagraphs.forEach(element => {
      element.textContent = ''; 
    });
    this.outputPositionParagraphs.forEach(element => {
      element.textContent = ''; 
    });

    this.outputFontParagraphs.forEach(element => {
      element.textContent = '';
    });
  }

  /**
   * Checks if all fields in the `personalizationData` object are filled (not null).
   * @returns {boolean} True if all personalization fields have a value, false otherwise.
   */
  checkFullObject() {
    let return_value = true;
    Object.values(this.personalizationData).forEach(value => {
      if(value == null) {
        return_value = false;
      }
    })
    return return_value;
  }

  /**
   * Enables or disables the personalization submit buttons.
   * Also adjusts cursor style to indicate interactivity.
   * @param {boolean} enabled If true, buttons are enabled; otherwise, they are disabled.
   */
  setButton(enabled = true){
    this.submitButtons.forEach(submitButton => {
      if(enabled) {
        submitButton.disabled = false;
        submitButton.style.cursor="pointer"
        
      } else {
        submitButton.disabled = true;
        submitButton.style.cursor="not-allowed"
      }
    })
  }

  changeButtonText (modify = true, text) {
    this.openDrawerButtons.forEach(openDrawerButton => {
        openDrawerButton.querySelector('span').textContent = text;
        openDrawerButton.style.border = modify ? "1px solid #FA531E" : "1px solid black";
    }) 
  }

  /**
   * Updates the text content of the final output elements displayed outside the personalization form.
   * Shows the selected color, position, and text, or clears the display if no personalization is selected.
   */
  setOuterData(){
    if(!this.personalizationData.color && !this.personalizationData.position && !this.personalizationData.text)
      this.finalOutputs.forEach(finalOutput => {
        finalOutput.textContent = '';
      })
    else
      this.finalOutputs.forEach(finalOutput => {
          finalOutput.textContent = `${this.personalizationData.color} / ${this.personalizationData.position} / ${this.personalizationData.text}`;
      })
  }

  setError(){
    console.warn("Error in click function");
  }
}

customElements.define('personalization-form', PersonalizationForm);